import { MONEY_SCALE, ZERO_MONEY, add, applyPercent, compare, isNegative, min, round, subtract, zero, } from '@aeris/money';
import { ZERO_AMOUNT, allowsChange, } from "./entities.js";
/**
 * Whether a payment counts toward what a document has been paid.
 *
 * `failed` does not: the row exists so a declined card is visible, not because
 * money moved. Everything else does — including `pending`, which is the case
 * that looks wrong and is not. A credit-card sale is *paid* at the counter and
 * *pending* at the acquirer; refusing to let the customer leave until the
 * acquirer settles in thirty days is not a policy any shop has ever had.
 */
export const countsTowardSettlement = (payment) => payment.status !== 'failed';
/**
 * Receipts minus refunds, at document scale.
 *
 * A refund is a row of its own with `direction: 'out'` — never an edit to the
 * receipt (ADR-0018) — so subtracting it here is what makes a partially
 * refunded document report what it is actually still holding.
 */
export const paidSoFar = (payments) => payments
    .filter(countsTowardSettlement)
    .reduce((running, payment) => payment.direction === 'in'
    ? add(running, payment.amount)
    : subtract(running, payment.amount), zero(MONEY_SCALE));
/** Cash actually handed over, minus cash already handed back. */
export const cashTendered = (payments) => paidSoFar(payments.filter((payment) => allowsChange(payment.methodKind)));
/**
 * How much change is due, and how much of the excess cannot be given as change.
 *
 * The rule is one line and it is the whole point of this file: **change comes
 * out of the drawer, so it can never exceed what went into the drawer.**
 *
 * Three cases, all of which happen daily:
 *
 *   * R$ 90,00 bill, R$ 100,00 in cash → R$ 10,00 change. The easy one.
 *   * R$ 90,00 bill, R$ 100,00 by PIX → no change and R$ 10,00 refused. PIX
 *     gives no change; the money is in the account and has to come back the
 *     way it arrived.
 *   * R$ 90,00 bill, R$ 85,00 by card and R$ 10,00 in cash → R$ 5,00 change,
 *     because that is all the cash there is. A naive `paid - total` would
 *     return the same R$ 5,00 here by luck; make the cash R$ 2,00 and the
 *     naive answer hands out three reais the drawer never had.
 */
export const changeFor = (total, payments) => {
    const excess = subtract(paidSoFar(payments), total);
    if (!isPositiveAmount(excess)) {
        return { changeDue: ZERO_AMOUNT, changeRefused: ZERO_AMOUNT };
    }
    const cash = cashTendered(payments);
    const available = isNegative(cash) ? ZERO_AMOUNT : cash;
    const changeDue = min(excess, available);
    return { changeDue, changeRefused: subtract(excess, changeDue) };
};
const isPositiveAmount = (value) => compare(value, ZERO_MONEY) > 0;
/**
 * The whole answer: settled, short or over, and what to do about it.
 *
 * `total` is the document's, and it is rounded to document scale here rather
 * than trusted, because a caller holding a sum of line totals at scale 6 would
 * otherwise be told it is a centavo short of itself.
 */
export const settlementOf = (total, payments) => {
    const documentTotal = round(total, MONEY_SCALE, 'half-up');
    const paid = paidSoFar(payments);
    const difference = subtract(paid, documentTotal);
    const outcome = isPositiveAmount(difference)
        ? 'over'
        : isNegative(difference)
            ? 'short'
            : 'settled';
    const { changeDue, changeRefused } = changeFor(documentTotal, payments);
    return {
        total: documentTotal,
        paid,
        outcome,
        outstanding: outcome === 'short' ? subtract(ZERO_AMOUNT, difference) : ZERO_AMOUNT,
        excess: outcome === 'over' ? difference : ZERO_AMOUNT,
        changeDue,
        changeRefused,
    };
};
// ---------------------------------------------------------------------------
// Fees
// ---------------------------------------------------------------------------
/**
 * The acquirer's cut on one payment.
 *
 * `feePercent` is quoted the way every Brazilian acquirer quotes it — 3,49%,
 * not 0,0349 — so the conversion happens once, here, rather than at each call
 * site where forgetting it is a factor of a hundred.
 *
 * Rounded to centavos with the commercial rule, because the fee is a monetary
 * amount that a bank statement will eventually have to match, not an
 * intermediate value.
 */
export const feeFor = (amount, feePercent) => applyPercent(amount, feePercent);
/** What the shop actually receives: the amount less the fee. */
export const netOf = (amount, feePercent) => subtract(amount, feeFor(amount, feePercent));
// ---------------------------------------------------------------------------
// Settlement timing
// ---------------------------------------------------------------------------
/**
 * When a payment taken now is expected to be in the account.
 *
 * Calendar days, not business days. Business days need a holiday calendar —
 * national, state and municipal, all three of which move — and a wrong one is
 * worse than an honest approximation, because it looks authoritative. The
 * column this feeds is named `expected_settlement_at` for the same reason: it
 * is an expectation, and reconciliation against the bank is what turns an
 * expectation into a fact.
 *
 * Returns null for a method that settles immediately: the money is already
 * there, and a date on it would invite somebody to wait for it.
 */
export const expectedSettlementAt = (receivedAt, method) => {
    if (method.settlesImmediately)
        return null;
    const expected = new Date(receivedAt.getTime());
    expected.setUTCDate(expected.getUTCDate() + method.settlementDays);
    return expected;
};
/**
 * The status a payment is written with.
 *
 * Decided from the method rather than supplied, because a caller that could
 * name the status could record a boleto as settled the day it was printed —
 * and the whole of the finance module downstream would believe it.
 */
export const statusOnRecord = (method) => (method.settlesImmediately ? 'settled' : 'pending');
//# sourceMappingURL=settlement.js.map